import {
  triggerToast,
  ButtonDS,
  DownloadOutlined,
  LaunchPage,
  usePersistMuiTableState,
  MaterialUiTable,
  MaterialUiColumnsProps,
  TextCapitalized,
  fromToDateFormatterDayMonthLongYear,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  documentDownloadAsync,
  selectSupportingDocumentsZipFolderFileExportLink,
  selectSupportingDocumentsZipFolderFileExportStatus,
  selectSupportingDocumentsZipFoldersList,
  selectSupportingDocumentsZipFoldersListStatus,
  supportingDocumentsZipFolderFileExportAsync,
  supportingDocumentsZipFoldersListAsync,
} from "../billingsSlice";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  SupportingDocumentsZipFoldersList,
  SupportingDocumentsZipFoldersListDisplayed,
} from "../billingsAPI";
import { selectCompany } from "../../homeSlice";
import "../../../../i18n/i18n";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import styled from "styled-components";
import { getDate } from "../../../../libSlice/materialUiFormats";

type SupportingDocumentsZipFolderRow = SupportingDocumentsZipFoldersList & {
  id: string;
};

export const SupportingDocumentsZipFoldersTab = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const params = useParams();
  const company = useAppSelector(selectCompany);

  const supportingDocumentsZipFoldersListInit = useAppSelector(
    selectSupportingDocumentsZipFoldersList,
  );
  const supportingDocumentsZipFoldersListStatus = useAppSelector(
    selectSupportingDocumentsZipFoldersListStatus,
  );
  const supportingDocumentsZipFolderFileExportStatus = useAppSelector(
    selectSupportingDocumentsZipFolderFileExportStatus,
  );
  const supportingDocumentsZipFolderFileExportLink = useAppSelector(
    selectSupportingDocumentsZipFolderFileExportLink,
  );
  const [listDisplayed, setListDisplayed] = useState<
    SupportingDocumentsZipFolderRow[] | null
  >(null);

  const pageName = "supportingDocumentsZipFolders";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      fromDate: "size",
      bucketFileName: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const supportingDocumentsZipFoldersList = useMemo(() => {
    return supportingDocumentsZipFoldersListInit.map((zipFolder) => ({
      ...zipFolder,
      id: zipFolder.uuid,
      fromDate: fromToDateFormatterDayMonthLongYear(
        new Date(zipFolder.fromDate),
        new Date(zipFolder.toDate),
      ),
    }));
  }, [supportingDocumentsZipFoldersListInit]);

  useEffect(() => {
    if (
      supportingDocumentsZipFoldersList &&
      supportingDocumentsZipFoldersListStatus === "success"
    ) {
      setListDisplayed(supportingDocumentsZipFoldersList);
    }
  }, [supportingDocumentsZipFoldersList, supportingDocumentsZipFoldersListStatus]);

  useEffect(() => {
    if (company && supportingDocumentsZipFoldersListStatus === "idle") {
      void dispatch(supportingDocumentsZipFoldersListAsync(company.uuid));
    }
  }, [dispatch, company, supportingDocumentsZipFoldersListStatus]);

  useEffect(() => {
    if (supportingDocumentsZipFolderFileExportStatus === "success") {
      triggerToast(t("billings.supportingDocuments.upload.success") || "", "valid");
    } else if (supportingDocumentsZipFolderFileExportStatus === "failed") {
      triggerToast(t("billings.supportingDocuments.upload.failure") || "", "error");
    }
  }, [supportingDocumentsZipFolderFileExportStatus, t]);

  useEffect(() => {
    if (
      supportingDocumentsZipFolderFileExportLink.presignedUrl &&
      supportingDocumentsZipFolderFileExportLink.fileName
    ) {
      void dispatch(
        documentDownloadAsync(supportingDocumentsZipFolderFileExportLink),
      );
    }
  }, [supportingDocumentsZipFolderFileExportLink, dispatch]);

  const columnsDisplayed: {
    [index in keyof SupportingDocumentsZipFoldersListDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      fromDate: {
        type: "date",
        width: 300,
        valueGetter: getDate,
        valueFormatter: (_value, row) => {
          const rowData = row as SupportingDocumentsZipFolderRow;
          return rowData.fromDate ? (
            <TextCapitalized>{rowData.fromDate}</TextCapitalized>
          ) : (
            ""
          );
        },
      },
      bucketFileName: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    const rowData = row as SupportingDocumentsZipFoldersList;
                    void dispatch(
                      supportingDocumentsZipFolderFileExportAsync({
                        bucketFileName: value,
                        fileName:
                          rowData.fileName ||
                          "QIVIA_" +
                            t("supportingDocumentsZipFolders.title") +
                            ".zip",
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
    };
  }, [dispatch, t]);

  if (params.tab !== "supportingDocuments") {
    return;
  }

  const searchBarProps = {
    values: supportingDocumentsZipFoldersList,
    setFilterValues: (filtered: SupportingDocumentsZipFoldersListDisplayed[]) =>
      setListDisplayed(filtered as SupportingDocumentsZipFolderRow[]),
    keysToIgnore: [
      "bucketFileName" as const,
      "uuid" as const,
      "toDate" as const,
    ],
  };

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <MaterialUiTable<
      keyof SupportingDocumentsZipFoldersListDisplayed,
      SupportingDocumentsZipFoldersListDisplayed
    >
      rows={listDisplayed}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
    />
  );
};

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;
